import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type DocumentStatus =
    | "processed"
    | "processing"
    | "post_processing"
    | "failed"
    | "pending";

interface StatusConfig {
    label: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    dotClass: string;
    pulse?: boolean;
}

const STATUS_MAP: Record<DocumentStatus, StatusConfig> = {
    processed: {
        label: "Processed",
        variant: "secondary",
        dotClass: "bg-emerald-500",
    },
    processing: {
        label: "Processing",
        variant: "secondary",
        dotClass: "bg-blue-500",
        pulse: true,
    },
    post_processing: {
        label: "Processing",
        variant: "secondary",
        dotClass: "bg-blue-500",
        pulse: true,
    },
    failed: {
        label: "Failed",
        variant: "destructive",
        dotClass: "bg-destructive",
    },
    pending: {
        label: "Pending",
        variant: "outline",
        dotClass: "bg-amber-500",
    },
};

const FALLBACK: StatusConfig = {
    label: "Unknown",
    variant: "outline",
    dotClass: "bg-muted-foreground",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getStatusConfig(
    status: DocumentStatus | null | undefined
): StatusConfig {
    if (!status || !STATUS_MAP[status]) return FALLBACK;
    return STATUS_MAP[status];
}

interface StatusBadgeProps {
    status: DocumentStatus | null | undefined;
}

export function StatusBadge({ status }: StatusBadgeProps) {
    const config = getStatusConfig(status as DocumentStatus);

    return (
        <Badge variant={config.variant} className="gap-1.5 font-normal">
            <span
                className={cn(
                    "size-1.5 rounded-full",
                    config.dotClass,
                    config.pulse && "animate-pulse"
                )}
            />
            {config.label}
        </Badge>
    );
}
